import {
  antecedenciaAplicavel,
  type EfeitoDeRegra,
  EFEITOS_DE_REGRA,
  fromWire,
  type Money,
  type RegraDeSazonalidade,
  ROTULO_DO_EFEITO,
  sazonalidadeDoDia,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CalendarClock,
  CircleCheck,
  CircleSlash,
  EllipsisVertical,
  Hourglass,
  Layers,
  type LucideIcon,
  Pencil,
  Plus,
  Sun,
  TrendingUp,
  TriangleAlert,
} from 'lucide-react'
import { type ReactElement, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { EmptyState } from '@/components/state/empty-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
// A categoria é da frota — a regra de preço só a referencia.
import { useCategoriasAtivas } from '@/features/frota/api'
import {
  escopoDaRegra,
  faixaDeAntecedencia,
  type FiltroDeAtivo,
  formatarMultiplicador,
  formatarPontosBaseComoPercentual,
  formatarVariacaoDoMultiplicador,
  type RegraDeAntecedencia,
  type RegraDeDiaDaSemana,
  type RegraDeOcupacao,
  rotuloDosDiasDaSemana,
  type Sazonalidade,
  type TabelaDePreco,
  useAtualizarRegraDeAntecedencia,
  useAtualizarRegraDeDiaDaSemana,
  useAtualizarRegraDeOcupacao,
  useAtualizarSazonalidade,
  useDesativarRegraDeAntecedencia,
  useDesativarRegraDeDiaDaSemana,
  useDesativarRegraDeOcupacao,
  useDesativarSazonalidade,
  usePoliticaDeLocacao,
  useRegrasDeAntecedencia,
  useRegrasDeDiaDaSemana,
  useRegrasDeOcupacao,
  useSazonalidades,
  useTabelasAtivas,
} from '@/features/precos/api'
import {
  type AlvoDaRegra,
  CAMADAS_DE_REGRA,
  type CamadaDeRegra,
  DialogoDeRegra,
  QUALQUER_TABELA,
  ROTULO_DA_CAMADA,
} from '@/features/precos/dialogo-de-regra'
import { errorMessage } from '@/lib/api'
import { formatarData, SEM_VALOR } from '@/lib/datas'
import { formatarMoedaDoWire } from '@/lib/dinheiro'
import { usePermissao } from '@/lib/permissoes'
import { cn } from '@/lib/utils'
import { CartaoDeInsight } from '@/features/ia/cartao-de-insight'

/**
 * Regras de preço — as quatro camadas que o motor aplica sobre a tarifa da
 * faixa.
 *
 * ---------------------------------------------------------------------------
 * A TELA EXISTE PARA QUE NENHUMA REGRA SEJA UM MISTÉRIO
 * ---------------------------------------------------------------------------
 * Regra de preço é código que o gestor escreve sem programar, e o pior desfecho
 * possível não é o erro: é a regra que EXISTE, aparece na lista, e não faz
 * nada. Ela acontece de três formas, e as três têm aviso próprio aqui:
 *
 *  1. SAZONALIDADE SOBREPOSTA. Duas regras podem cobrir o mesmo dia e só uma
 *     vence — a de maior prioridade e, no empate, a de período mais curto. Um
 *     feriado com prioridade 0 criado dentro da alta temporada com prioridade
 *     10 nunca se aplica. A coluna "Sobreposição" diz isso na cara, e quem
 *     decide o vencedor é `sazonalidadeDoDia` do `@locaveri/shared` — a MESMA
 *     função que o motor chama. Reimplementar o desempate aqui daria uma tela
 *     que discorda do preço.
 *
 *  2. DIA DA SEMANA EM DOBRO. Duas regras ativas que marcam sexta não se somam:
 *     o motor aplica uma só, e qual delas depende da ordem em que o banco
 *     devolveu as linhas. É âmbar porque é ambiguidade, não desenho.
 *
 *  3. OCUPAÇÃO DESLIGADA. A quinta camada nasce fechada na política de locação,
 *     e enquanto ela estiver fechada o motor NEM CONSULTA estas regras. A aba
 *     avisa antes da lista — uma regra que existe e não age é a pior forma de
 *     confundir.
 *
 * ---------------------------------------------------------------------------
 * O AMARELO APARECE UMA VEZ E MEIA
 * ---------------------------------------------------------------------------
 * O CTA "Nova regra" e o traço embaixo da aba ativa. Nada mais: a tela é uma
 * grade de multiplicadores, e se cada fator virasse amarelo o botão que salva a
 * regra perderia o único lugar onde o olho deve parar. Aviso é âmbar
 * (`warning`), que é visualmente distinto do amarelo da marca.
 *
 * ---------------------------------------------------------------------------
 * ARITMÉTICA
 * ---------------------------------------------------------------------------
 * Nenhuma aqui. Multiplicador e ocupação chegam em PONTOS-BASE inteiros e viram
 * texto por `formatarMultiplicador`/`formatarPontosBaseComoPercentual`;
 * dinheiro chega como string de centavos e passa por `formatarMoedaDoWire`.
 * Nenhum valor monetário vira `number` neste arquivo (invariante nº 1), e
 * preço nenhum é calculado nele (invariante nº 3).
 */

const TAMANHO_DA_PAGINA = 20

/** O ícone de cada camada é o mesmo do detalhamento da cotação (`badges.tsx`). */
const ICONE_DA_CAMADA: Readonly<Record<CamadaDeRegra, LucideIcon>> = {
  SAZONALIDADE: Sun,
  DIA_DA_SEMANA: CalendarClock,
  ANTECEDENCIA: Hourglass,
  OCUPACAO: TrendingUp,
}

/** O que cada aba lista, em uma linha, abaixo do título. */
const RESUMO_DA_CAMADA: Readonly<Record<CamadaDeRegra, string>> = {
  SAZONALIDADE:
    'Períodos com preço próprio. Quando duas cobrem o mesmo dia, só uma vale — a de maior prioridade.',
  DIA_DA_SEMANA:
    'Multiplicador por dia da semana, aplicado sobre o que a sazonalidade já produziu naquele dia.',
  ANTECEDENCIA:
    'Early bird e last minute pela distância entre a cotação e a retirada. Vence a faixa mais estreita.',
  OCUPACAO:
    'Multiplicador por ocupação prevista da categoria. Só age com o preço por ocupação ligado na política.',
}

const OPCOES_DE_ATIVO = ['true', 'false'] as const satisfies readonly FiltroDeAtivo[]

/**
 * As duas palavras da vigência, num lugar só.
 *
 * O filtro e o selo da linha precisam dizer a MESMA coisa: "Em vigor" no
 * seletor e "Ativa" na tabela seriam dois nomes para o mesmo booleano, e quem
 * filtrasse por um não reconheceria o outro na lista que voltasse.
 */
const ROTULO_DA_VIGENCIA: Readonly<Record<(typeof OPCOES_DE_ATIVO)[number], string>> = {
  true: 'Em vigor',
  false: 'Fora de vigor',
}

// ---------------------------------------------------------------------------
// Sobreposição de sazonalidade — quem venceria, pelo critério do motor
// ---------------------------------------------------------------------------

/** Centavos de uma string de wire; ilegível vira `null` em vez de derrubar a linha. */
function valorDoMotor(valor: string | null): Money | null {
  if (valor === null) {
    return null
  }

  try {
    return fromWire(valor)
  } catch {
    return null
  }
}

/**
 * O modelo de leitura na forma que o motor entende.
 *
 * O desempate não olha para `valorFixo`, mas o tipo do motor o exige e mentir
 * para ele (mandar `null` sempre) seria a linha que alguém reaproveitaria um
 * dia para calcular preço com valor nenhum.
 */
function paraOMotor(regra: Sazonalidade): RegraDeSazonalidade {
  return {
    id: regra.id,
    nome: regra.nome,
    inicio: regra.inicio,
    fim: regra.fim,
    efeito: regra.efeito,
    multiplicadorEmPontosBase: regra.multiplicadorEmPontosBase,
    valorFixo: valorDoMotor(regra.valorFixo),
    prioridade: regra.prioridade,
  }
}

/**
 * Duas regras só disputam um dia quando podem valer na MESMA cotação: escopo
 * nulo é "todas", e por isso ele cruza com qualquer coisa.
 */
function escoposSeCruzam(uma: Sazonalidade, outra: Sazonalidade): boolean {
  const categoria =
    uma.categoryId === null || outra.categoryId === null || uma.categoryId === outra.categoryId
  const tabela =
    uma.priceTableId === null ||
    outra.priceTableId === null ||
    uma.priceTableId === outra.priceTableId

  return categoria && tabela
}

/** `AAAA-MM-DD` compara como texto na mesma ordem em que compara como data. */
function periodosSeCruzam(uma: Sazonalidade, outra: Sazonalidade): boolean {
  return uma.inicio <= outra.fim && outra.inicio <= uma.fim
}

/** O escopo de `ampla` engloba o de `estreita` — "todas as categorias" engloba uma. */
function escopoEngloba(ampla: Sazonalidade, estreita: Sazonalidade): boolean {
  const categoria = ampla.categoryId === null || ampla.categoryId === estreita.categoryId
  const tabela = ampla.priceTableId === null || ampla.priceTableId === estreita.priceTableId

  return categoria && tabela
}

/**
 * Qual das duas o MOTOR escolheria num dia coberto pelas duas.
 *
 * A pergunta é feita ao `sazonalidadeDoDia` do `@locaveri/shared` no primeiro
 * dia em comum — maior prioridade, empate desfeito pelo período mais curto e,
 * ainda empatado, pelo `id`. Reescrever esse critério aqui era a alternativa, e
 * ela discordaria do preço no dia em que o motor mudasse de ideia.
 */
function vencedoraEntre(uma: Sazonalidade, outra: Sazonalidade): Sazonalidade {
  const diaEmComum = uma.inicio > outra.inicio ? uma.inicio : outra.inicio
  const escolhida = sazonalidadeDoDia(diaEmComum, [paraOMotor(uma), paraOMotor(outra)])

  return escolhida?.id === outra.id ? outra : uma
}

interface ConflitoDeSazonalidade {
  /** Regras que vencem esta nos dias em comum. */
  readonly perdePara: readonly Sazonalidade[]
  /** Regras que esta vence nos dias em comum. */
  readonly venceSobre: readonly Sazonalidade[]
  /** Cobre o período INTEIRO, engloba o escopo e vence: a regra nunca se aplica. */
  readonly sombreadaPor: Sazonalidade | null
}

const SEM_CONFLITO: ConflitoDeSazonalidade = {
  perdePara: [],
  venceSobre: [],
  sombreadaPor: null,
}

/**
 * O mapa de sobreposições da PÁGINA carregada.
 *
 * Só regras em vigor entram na conta — uma regra fora de vigor não disputa dia
 * nenhum, e marcá-la como perdedora esconderia o motivo real de ela não estar
 * valendo. A comparação é aos pares e quadrática, sobre 20 linhas: irrelevante.
 */
function analisarSobreposicoes(
  itens: readonly Sazonalidade[],
): ReadonlyMap<string, ConflitoDeSazonalidade> {
  const emVigor = itens.filter((regra) => regra.ativo)
  const mapa = new Map<string, ConflitoDeSazonalidade>()

  for (const regra of emVigor) {
    const perdePara: Sazonalidade[] = []
    const venceSobre: Sazonalidade[] = []
    let sombreadaPor: Sazonalidade | null = null

    for (const outra of emVigor) {
      if (
        outra.id === regra.id ||
        !escoposSeCruzam(regra, outra) ||
        !periodosSeCruzam(regra, outra)
      ) {
        continue
      }

      if (vencedoraEntre(regra, outra).id === regra.id) {
        venceSobre.push(outra)
        continue
      }

      perdePara.push(outra)

      const cobreTudo = outra.inicio <= regra.inicio && outra.fim >= regra.fim

      if (sombreadaPor === null && cobreTudo && escopoEngloba(outra, regra)) {
        sombreadaPor = outra
      }
    }

    mapa.set(regra.id, { perdePara, venceSobre, sombreadaPor })
  }

  return mapa
}

/** Quantos dias de calendário a regra cobre — o critério de desempate do motor. */
function diasDoPeriodo(inicio: string, fim: string): number | null {
  const primeiro = Date.parse(`${inicio}T00:00:00Z`)
  const ultimo = Date.parse(`${fim}T00:00:00Z`)

  if (Number.isNaN(primeiro) || Number.isNaN(ultimo) || ultimo < primeiro) {
    return null
  }

  return Math.round((ultimo - primeiro) / 86_400_000) + 1
}

/**
 * Regras de dia da semana que marcam algum dia em comum com esta.
 *
 * O motor aplica UMA regra de dia da semana por diária (ele pega a primeira que
 * casa) — duas que marcam sexta não se compõem, e a que vale depende da ordem
 * em que a listagem chegou. Não dá para dizer qual vence; dá para dizer que a
 * pergunta não tem resposta, que é a informação que evita a regra fantasma.
 */
function conflitosDeDiaDaSemana(
  itens: readonly RegraDeDiaDaSemana[],
): ReadonlyMap<string, readonly RegraDeDiaDaSemana[]> {
  const emVigor = itens.filter((regra) => regra.ativo)
  const mapa = new Map<string, readonly RegraDeDiaDaSemana[]>()

  for (const regra of emVigor) {
    const concorrentes = emVigor.filter(
      (outra) =>
        outra.id !== regra.id &&
        (outra.categoryId === null ||
          regra.categoryId === null ||
          outra.categoryId === regra.categoryId) &&
        outra.diasDaSemana.some((dia) => regra.diasDaSemana.includes(dia)),
    )

    mapa.set(regra.id, concorrentes)
  }

  return mapa
}

/**
 * Regras de antecedência que cobrem alguma distância em comum com esta e a
 * vencem.
 *
 * Quem decide é `antecedenciaAplicavel` do `@locaveri/shared`, no primeiro dia
 * de sobreposição: faixa mais estreita vence, porque faixa estreita é regra
 * mais específica. O modelo de leitura já tem a forma que o motor pede.
 */
function conflitosDeAntecedencia(
  itens: readonly RegraDeAntecedencia[],
): ReadonlyMap<string, readonly RegraDeAntecedencia[]> {
  const emVigor = itens.filter((regra) => regra.ativo)
  const mapa = new Map<string, readonly RegraDeAntecedencia[]>()

  for (const regra of emVigor) {
    const perdePara = emVigor.filter((outra) => {
      if (outra.id === regra.id) {
        return false
      }

      const mesmoEscopo =
        outra.categoryId === null ||
        regra.categoryId === null ||
        outra.categoryId === regra.categoryId

      const inicioEmComum = Math.max(regra.minimoEmDias, outra.minimoEmDias)
      const tetoDaRegra = regra.maximoEmDias ?? Number.MAX_SAFE_INTEGER
      const tetoDaOutra = outra.maximoEmDias ?? Number.MAX_SAFE_INTEGER

      if (!mesmoEscopo || inicioEmComum > Math.min(tetoDaRegra, tetoDaOutra)) {
        return false
      }

      return antecedenciaAplicavel(inicioEmComum, [regra, outra])?.id === outra.id
    })

    mapa.set(regra.id, perdePara)
  }

  return mapa
}

/** Faixas de ocupação que se cruzam — na sobreposição só uma vale. */
function conflitosDeOcupacao(
  itens: readonly RegraDeOcupacao[],
): ReadonlyMap<string, readonly RegraDeOcupacao[]> {
  const emVigor = itens.filter((regra) => regra.ativo)
  const mapa = new Map<string, readonly RegraDeOcupacao[]>()

  for (const regra of emVigor) {
    const cruzam = emVigor.filter(
      (outra) =>
        outra.id !== regra.id &&
        (outra.categoryId === null ||
          regra.categoryId === null ||
          outra.categoryId === regra.categoryId) &&
        regra.ocupacaoMinimaEmPontosBase <= outra.ocupacaoMaximaEmPontosBase &&
        outra.ocupacaoMinimaEmPontosBase <= regra.ocupacaoMaximaEmPontosBase,
    )

    mapa.set(regra.id, cruzam)
  }

  return mapa
}

// ---------------------------------------------------------------------------
// Células compartilhadas pelas quatro abas
// ---------------------------------------------------------------------------

function BadgeDeVigencia({ ativo }: { readonly ativo: boolean }): ReactElement {
  return ativo ? (
    <Badge variant="success">
      <CircleCheck aria-hidden="true" />
      {ROTULO_DA_VIGENCIA.true}
    </Badge>
  ) : (
    <Badge variant="neutral">
      <CircleSlash aria-hidden="true" />
      {ROTULO_DA_VIGENCIA.false}
    </Badge>
  )
}

/** Nome da regra e, embaixo, o que ela alcança. */
function CelulaDaRegra({
  nome,
  escopo,
}: {
  readonly nome: string
  readonly escopo: string
}): ReactElement {
  return (
    <div className="min-w-0">
      <p className="truncate font-medium text-foreground">{nome}</p>
      <p className="truncate text-xs text-fg-secondary">{escopo}</p>
    </div>
  )
}

/**
 * O fator e o que ele faz com o preço, um embaixo do outro.
 *
 * `1,25×` responde "que fator é este" e `+25%` responde "encarece quanto" — o
 * gestor confere pela segunda linha, e é ela que denuncia um fator 100 digitado
 * por engano.
 */
function CelulaDeMultiplicador({ pontosBase }: { readonly pontosBase: number }): ReactElement {
  return (
    <div className="min-w-0">
      <p className="money-cell text-foreground">{formatarMultiplicador(pontosBase)}</p>
      <p className="whitespace-nowrap text-xs tabular-money text-fg-secondary">
        {formatarVariacaoDoMultiplicador(pontosBase)}
      </p>
    </div>
  )
}

interface AcoesDaRegraProps {
  readonly nome: string
  readonly ativa: boolean
  readonly podeEditar: boolean
  readonly podeDesativar: boolean
  readonly ocupado: boolean
  readonly aoEditar: () => void
  readonly aoAlternar: () => void
}

/**
 * Editar e tirar/pôr em vigor.
 *
 * Não existe apagar: uma regra desativada continua explicando por que a
 * cotação de março cobrou o que cobrou. Sem nenhuma permissão aplicável a
 * célula vira traço — botão que leva a 403 é promessa que não se cumpre.
 *
 * PÔR e TIRAR de vigor parecem a mesma ação e não são, porque não são o mesmo
 * verbo na API: pôr em vigor é `PATCH { ativo: true }` (`price_table.update`) e
 * tirar de vigor é `DELETE` (`price_table.delete`). Por isso a permissão
 * consultada depende do estado ATUAL da regra. Um booleano só para as duas
 * errava dos dois lados: o FINANCEIRO, que tem `update` sem `delete`, não via a
 * ação que podia executar, e um papel com `delete` sem `update` via "Pôr em
 * vigor" para levar 403 no clique.
 */
function AcoesDaRegra({
  nome,
  ativa,
  podeEditar,
  podeDesativar,
  ocupado,
  aoEditar,
  aoAlternar,
}: AcoesDaRegraProps): ReactElement {
  const podeAlternar = ativa ? podeDesativar : podeEditar

  if (!podeEditar && !podeAlternar) {
    return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Ações da regra ${nome}`}
          className="size-8"
          disabled={ocupado}
        >
          <EllipsisVertical aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        {podeEditar ? (
          <DropdownMenuItem onSelect={aoEditar}>
            <Pencil aria-hidden="true" />
            Editar regra
          </DropdownMenuItem>
        ) : null}

        {podeAlternar ? (
          <>
            {podeEditar ? <DropdownMenuSeparator /> : null}
            <DropdownMenuItem onSelect={aoAlternar}>
              {ativa ? <CircleSlash aria-hidden="true" /> : <CircleCheck aria-hidden="true" />}
              {ativa ? 'Tirar de vigor' : 'Pôr em vigor'}
            </DropdownMenuItem>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/** Mensagens de toast das quatro abas — a frase é a mesma, a regra é que muda. */
function avisarVigencia(nome: string, ativa: boolean): void {
  toast.success(ativa ? `“${nome}” saiu de vigor` : `“${nome}” voltou a valer`, {
    description: ativa
      ? 'O motor deixa de consultá-la. Cotação já emitida continua com o preço que tinha.'
      : 'Vale da próxima cotação em diante.',
  })
}

function avisarFalhaDeVigencia(causa: unknown): void {
  toast.error('Não foi possível mudar a vigência da regra', { description: errorMessage(causa) })
}

// ---------------------------------------------------------------------------
// Props comuns das abas
// ---------------------------------------------------------------------------

interface PropsDaAba {
  readonly pagina: number
  readonly aoMudarDePagina: (pagina: number) => void
  readonly categoryId: string
  readonly ativo: FiltroDeAtivo
  readonly temFiltro: boolean
  readonly aoLimparFiltros: () => void
  readonly podeCriar: boolean
  readonly podeEditar: boolean
  readonly podeDesativar: boolean
  readonly aoCriar: () => void
}

// ---------------------------------------------------------------------------
// Aba 1 — sazonalidade
// ---------------------------------------------------------------------------

interface PropsDaAbaDeSazonalidades extends PropsDaAba {
  readonly priceTableId: string
  readonly efeito: EfeitoDeRegra | ''
  readonly vigenteEm: string
  readonly tabelas: readonly TabelaDePreco[]
  readonly aoEditar: (regra: Sazonalidade) => void
}

function AbaDeSazonalidades({
  pagina,
  aoMudarDePagina,
  categoryId,
  ativo,
  priceTableId,
  efeito,
  vigenteEm,
  tabelas,
  temFiltro,
  aoLimparFiltros,
  podeCriar,
  podeEditar,
  podeDesativar,
  aoCriar,
  aoEditar,
}: PropsDaAbaDeSazonalidades): ReactElement {
  const filtros = useMemo(
    () => ({
      pagina,
      tamanho: TAMANHO_DA_PAGINA,
      priceTableId,
      categoryId,
      efeito,
      ativo,
      vigenteEm,
    }),
    [pagina, priceTableId, categoryId, efeito, ativo, vigenteEm],
  )

  const consulta = useSazonalidades(filtros)
  const desativar = useDesativarSazonalidade()
  const atualizar = useAtualizarSazonalidade()

  const itens = useMemo(() => consulta.data?.itens ?? [], [consulta.data])
  const conflitos = useMemo(() => analisarSobreposicoes(itens), [itens])

  const ocupado = desativar.isPending || atualizar.isPending

  function alternarVigencia(regra: Sazonalidade): void {
    const opcoes = {
      onSuccess: () => {
        avisarVigencia(regra.nome, regra.ativo)
      },
      onError: avisarFalhaDeVigencia,
    }

    if (regra.ativo) {
      desativar.mutate(regra.id, opcoes)
      return
    }

    atualizar.mutate({ id: regra.id, dados: { ativo: true } }, opcoes)
  }

  /**
   * `null` = qualquer tabela. O rótulo é o mesmo que o formulário usa na opção
   * vazia — a lista e o diálogo não podem chamar o mesmo escopo de dois nomes.
   * Tabela desativada não está na lista de ativas e cai no genérico.
   */
  function nomeDaTabela(id: string | null): string {
    if (id === null) {
      return QUALQUER_TABELA
    }

    return tabelas.find((tabela) => tabela.id === id)?.nome ?? 'Tabela específica'
  }

  const colunas = useMemo<ColumnDef<Sazonalidade>[]>(
    () => [
      {
        id: 'nome',
        header: 'Regra',
        accessorFn: (linha: Sazonalidade) => linha.nome,
        cell: ({ row }) => (
          <CelulaDaRegra
            nome={row.original.nome}
            escopo={`${escopoDaRegra(row.original.categoria)} · ${nomeDaTabela(row.original.priceTableId)}`}
          />
        ),
      },
      {
        id: 'periodo',
        header: 'Período',
        accessorFn: (linha: Sazonalidade) => linha.inicio,
        cell: ({ row }) => {
          const regra: Sazonalidade = row.original
          const dias = diasDoPeriodo(regra.inicio, regra.fim)

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap text-sm tabular-money text-foreground">
                {formatarData(regra.inicio)} → {formatarData(regra.fim)}
              </p>
              <p className="whitespace-nowrap text-xs tabular-money text-fg-secondary">
                {dias === null ? SEM_VALOR : `${String(dias)} dias, o último inclusive`}
              </p>
            </div>
          )
        },
      },
      {
        id: 'efeito',
        header: 'Efeito',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Sazonalidade) => ROTULO_DO_EFEITO[linha.efeito],
        cell: ({ row }) => {
          const regra: Sazonalidade = row.original

          if (regra.efeito === 'FIXO') {
            return (
              <div className="min-w-0">
                <p className="money-cell text-foreground">{formatarMoedaDoWire(regra.valorFixo)}</p>
                <p className="whitespace-nowrap text-xs text-fg-secondary">
                  substitui a diária da faixa
                </p>
              </div>
            )
          }

          return <CelulaDeMultiplicador pontosBase={regra.multiplicadorEmPontosBase} />
        },
      },
      {
        id: 'prioridade',
        header: 'Prioridade',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: Sazonalidade) => linha.prioridade,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="money-cell text-foreground">{row.original.prioridade}</p>
            <p className="whitespace-nowrap text-xs text-fg-secondary">maior vence</p>
          </div>
        ),
      },
      {
        id: 'sobreposicao',
        header: 'Sobreposição',
        enableSorting: false,
        cell: ({ row }) => {
          const regra: Sazonalidade = row.original

          if (!regra.ativo) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          const conflito = conflitos.get(regra.id) ?? SEM_CONFLITO

          if (conflito.sombreadaPor !== null) {
            const dona = conflito.sombreadaPor

            return (
              <div className="min-w-0 space-y-1">
                <Badge variant="warning">
                  <TriangleAlert aria-hidden="true" />
                  Nunca se aplica
                </Badge>
                <p className="max-w-56 text-xs text-fg-secondary">
                  “{dona.nome}” cobre todo o período com prioridade {dona.prioridade} e vence em
                  todos os dias desta regra. Para esta valer, dê a ela prioridade acima de{' '}
                  {dona.prioridade}.
                </p>
              </div>
            )
          }

          if (conflito.perdePara.length > 0) {
            const [primeira, ...resto] = conflito.perdePara

            return (
              <div className="min-w-0 space-y-1">
                <Badge variant="neutral">
                  <Layers aria-hidden="true" />
                  Perde nos dias em comum
                </Badge>
                <p className="max-w-56 text-xs text-fg-secondary">
                  Nesses dias vale “{primeira?.nome ?? SEM_VALOR}”
                  {resto.length > 0 ? ` e mais ${String(resto.length)}` : ''}.
                </p>
              </div>
            )
          }

          if (conflito.venceSobre.length > 0) {
            const [primeira, ...resto] = conflito.venceSobre

            return (
              <div className="min-w-0 space-y-1">
                <Badge variant="outline">
                  <Layers aria-hidden="true" />
                  Vence nos dias em comum
                </Badge>
                <p className="max-w-56 text-xs text-fg-secondary">
                  Sobrepõe “{primeira?.nome ?? SEM_VALOR}”
                  {resto.length > 0 ? ` e mais ${String(resto.length)}` : ''}.
                </p>
              </div>
            )
          }

          return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
        },
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeVigencia ativo={row.original.ativo} />,
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <AcoesDaRegra
            nome={row.original.nome}
            ativa={row.original.ativo}
            podeEditar={podeEditar}
            podeDesativar={podeDesativar}
            ocupado={ocupado}
            aoEditar={() => {
              aoEditar(row.original)
            }}
            aoAlternar={() => {
              alternarVigencia(row.original)
            }}
          />
        ),
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas são as permissões, o mapa de sobreposições (que a
    // célula lê) e o estado de envio das mutações.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesativar, ocupado, conflitos, tabelas],
  )

  const dados = consulta.data

  return (
    <div className="flex flex-col gap-4">
      <Alert variant="info">
        <AlertTitle>Duas regras podem cobrir o mesmo dia — só uma vale</AlertTitle>
        <AlertDescription>
          Vence a de maior prioridade e, no empate, a de período mais curto: é assim que o Réveillon
          convive com a alta temporada. A coluna “Sobreposição” confere as regras{' '}
          <strong>desta página</strong> pelo mesmo critério do motor de preço.
          {vigenteEm.length > 0
            ? ` A lista está restrita às regras que cobrem ${formatarData(vigenteEm)}.`
            : ''}
        </AlertDescription>
      </Alert>

      <TabelaDeDados
        colunas={colunas}
        dados={itens}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Regras de sazonalidade"
        ordenacaoInicial={[{ id: 'prioridade', desc: true }]}
        estadoVazio={
          <EmptyState
            icone={Sun}
            titulo={temFiltro ? 'Nenhuma sazonalidade com esses filtros' : 'Nenhuma sazonalidade'}
            descricao={
              temFiltro
                ? 'Ajuste a categoria, a tabela ou o dia para ver outras regras.'
                : 'Sem regra de período, toda diária sai pela tarifa da faixa — Réveillon pelo preço de terça-feira.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={aoLimparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={aoCriar}>
                  <Plus aria-hidden="true" />
                  Nova regra
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={aoMudarDePagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aba 2 — dia da semana
// ---------------------------------------------------------------------------

interface PropsDaAbaDeDiasDaSemana extends PropsDaAba {
  readonly aoEditar: (regra: RegraDeDiaDaSemana) => void
}

function AbaDeDiasDaSemana({
  pagina,
  aoMudarDePagina,
  categoryId,
  ativo,
  temFiltro,
  aoLimparFiltros,
  podeCriar,
  podeEditar,
  podeDesativar,
  aoCriar,
  aoEditar,
}: PropsDaAbaDeDiasDaSemana): ReactElement {
  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, categoryId, ativo }),
    [pagina, categoryId, ativo],
  )

  const consulta = useRegrasDeDiaDaSemana(filtros)
  const desativar = useDesativarRegraDeDiaDaSemana()
  const atualizar = useAtualizarRegraDeDiaDaSemana()

  const itens = useMemo(() => consulta.data?.itens ?? [], [consulta.data])
  const conflitos = useMemo(() => conflitosDeDiaDaSemana(itens), [itens])

  const ocupado = desativar.isPending || atualizar.isPending

  function alternarVigencia(regra: RegraDeDiaDaSemana): void {
    const opcoes = {
      onSuccess: () => {
        avisarVigencia(regra.nome, regra.ativo)
      },
      onError: avisarFalhaDeVigencia,
    }

    if (regra.ativo) {
      desativar.mutate(regra.id, opcoes)
      return
    }

    atualizar.mutate({ id: regra.id, dados: { ativo: true } }, opcoes)
  }

  const colunas = useMemo<ColumnDef<RegraDeDiaDaSemana>[]>(
    () => [
      {
        id: 'nome',
        header: 'Regra',
        accessorFn: (linha: RegraDeDiaDaSemana) => linha.nome,
        cell: ({ row }) => (
          <CelulaDaRegra nome={row.original.nome} escopo={escopoDaRegra(row.original.categoria)} />
        ),
      },
      {
        id: 'dias',
        header: 'Dias',
        accessorFn: (linha: RegraDeDiaDaSemana) => rotuloDosDiasDaSemana(linha.diasDaSemana),
        cell: ({ row }) => {
          const regra: RegraDeDiaDaSemana = row.original
          const concorrentes = regra.ativo ? (conflitos.get(regra.id) ?? []) : []
          const [primeira, ...resto] = concorrentes

          return (
            <div className="min-w-0 space-y-1">
              <p className="text-sm text-foreground">{rotuloDosDiasDaSemana(regra.diasDaSemana)}</p>

              {primeira === undefined ? null : (
                <>
                  <Badge variant="warning">
                    <TriangleAlert aria-hidden="true" />
                    Dia disputado
                  </Badge>
                  <p className="max-w-56 text-xs text-fg-secondary">
                    “{primeira.nome}”{resto.length > 0 ? ` e mais ${String(resto.length)}` : ''}{' '}
                    marca os mesmos dias. O motor aplica só uma das regras, e a escolha não é sua.
                  </p>
                </>
              )}
            </div>
          )
        },
      },
      {
        id: 'multiplicador',
        header: 'Multiplicador',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: RegraDeDiaDaSemana) => linha.multiplicadorEmPontosBase,
        cell: ({ row }) => (
          <CelulaDeMultiplicador pontosBase={row.original.multiplicadorEmPontosBase} />
        ),
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeVigencia ativo={row.original.ativo} />,
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <AcoesDaRegra
            nome={row.original.nome}
            ativa={row.original.ativo}
            podeEditar={podeEditar}
            podeDesativar={podeDesativar}
            ocupado={ocupado}
            aoEditar={() => {
              aoEditar(row.original)
            }}
            aoAlternar={() => {
              alternarVigencia(row.original)
            }}
          />
        ),
      },
    ],
    // Ver a nota da aba de sazonalidade: o que muda as colunas são as
    // permissões, o mapa de conflitos e o estado de envio.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesativar, ocupado, conflitos],
  )

  const dados = consulta.data

  return (
    <div className="flex flex-col gap-4">
      <TabelaDeDados
        colunas={colunas}
        dados={itens}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Regras de dia da semana"
        estadoVazio={
          <EmptyState
            icone={CalendarClock}
            titulo={
              temFiltro ? 'Nenhuma regra com esses filtros' : 'Nenhuma regra de dia da semana'
            }
            descricao={
              temFiltro
                ? 'Ajuste a categoria ou a situação para ver outras regras.'
                : 'É aqui que sexta e sábado ficam mais caros que terça, sem mexer na tabela.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={aoLimparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={aoCriar}>
                  <Plus aria-hidden="true" />
                  Nova regra
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={aoMudarDePagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aba 3 — antecedência
// ---------------------------------------------------------------------------

interface PropsDaAbaDeAntecedencias extends PropsDaAba {
  readonly aoEditar: (regra: RegraDeAntecedencia) => void
}

function AbaDeAntecedencias({
  pagina,
  aoMudarDePagina,
  categoryId,
  ativo,
  temFiltro,
  aoLimparFiltros,
  podeCriar,
  podeEditar,
  podeDesativar,
  aoCriar,
  aoEditar,
}: PropsDaAbaDeAntecedencias): ReactElement {
  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, categoryId, ativo }),
    [pagina, categoryId, ativo],
  )

  const consulta = useRegrasDeAntecedencia(filtros)
  const desativar = useDesativarRegraDeAntecedencia()
  const atualizar = useAtualizarRegraDeAntecedencia()

  const itens = useMemo(() => consulta.data?.itens ?? [], [consulta.data])
  const conflitos = useMemo(() => conflitosDeAntecedencia(itens), [itens])

  const ocupado = desativar.isPending || atualizar.isPending

  function alternarVigencia(regra: RegraDeAntecedencia): void {
    const opcoes = {
      onSuccess: () => {
        avisarVigencia(regra.nome, regra.ativo)
      },
      onError: avisarFalhaDeVigencia,
    }

    if (regra.ativo) {
      desativar.mutate(regra.id, opcoes)
      return
    }

    atualizar.mutate({ id: regra.id, dados: { ativo: true } }, opcoes)
  }

  const colunas = useMemo<ColumnDef<RegraDeAntecedencia>[]>(
    () => [
      {
        id: 'nome',
        header: 'Regra',
        accessorFn: (linha: RegraDeAntecedencia) => linha.nome,
        cell: ({ row }) => (
          <CelulaDaRegra nome={row.original.nome} escopo={escopoDaRegra(row.original.categoria)} />
        ),
      },
      {
        id: 'faixa',
        header: 'Antecedência',
        accessorFn: (linha: RegraDeAntecedencia) => linha.minimoEmDias,
        cell: ({ row }) => {
          const regra: RegraDeAntecedencia = row.original
          const perdePara = regra.ativo ? (conflitos.get(regra.id) ?? []) : []
          const [primeira, ...resto] = perdePara

          return (
            <div className="min-w-0 space-y-1">
              <p className="whitespace-nowrap text-sm tabular-money text-foreground">
                {faixaDeAntecedencia(regra)}
              </p>

              {primeira === undefined ? null : (
                <>
                  <Badge variant="neutral">
                    <Layers aria-hidden="true" />
                    Perde na sobreposição
                  </Badge>
                  <p className="max-w-56 text-xs text-fg-secondary">
                    Onde as faixas se cruzam vale “{primeira.nome}”
                    {resto.length > 0 ? ` e mais ${String(resto.length)}` : ''} — faixa mais
                    estreita é regra mais específica.
                  </p>
                </>
              )}
            </div>
          )
        },
      },
      {
        id: 'multiplicador',
        header: 'Multiplicador',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: RegraDeAntecedencia) => linha.multiplicadorEmPontosBase,
        cell: ({ row }) => (
          <CelulaDeMultiplicador pontosBase={row.original.multiplicadorEmPontosBase} />
        ),
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeVigencia ativo={row.original.ativo} />,
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <AcoesDaRegra
            nome={row.original.nome}
            ativa={row.original.ativo}
            podeEditar={podeEditar}
            podeDesativar={podeDesativar}
            ocupado={ocupado}
            aoEditar={() => {
              aoEditar(row.original)
            }}
            aoAlternar={() => {
              alternarVigencia(row.original)
            }}
          />
        ),
      },
    ],
    // Ver a nota da aba de sazonalidade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesativar, ocupado, conflitos],
  )

  const dados = consulta.data

  return (
    <div className="flex flex-col gap-4">
      <TabelaDeDados
        colunas={colunas}
        dados={itens}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Regras de antecedência"
        estadoVazio={
          <EmptyState
            icone={Hourglass}
            titulo={temFiltro ? 'Nenhuma regra com esses filtros' : 'Nenhuma regra de antecedência'}
            descricao={
              temFiltro
                ? 'Ajuste a categoria ou a situação para ver outras regras.'
                : 'Sem elas, reservar com três meses e reservar para amanhã custam a mesma coisa.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={aoLimparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={aoCriar}>
                  <Plus aria-hidden="true" />
                  Nova regra
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={aoMudarDePagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aba 4 — ocupação
// ---------------------------------------------------------------------------

/**
 * O que a tela sabe sobre o interruptor de preço por ocupação da política.
 *
 * Um `boolean | null` não dava conta: ele misturava "ainda estou lendo" com
 * "não consegui ler", e as duas situações pedem coisas opostas da tela — a
 * primeira é silêncio de meio segundo, a segunda é um aviso de que a informação
 * FALTA. `SEM_ACESSO` é o terceiro caso e é o mais comum dos três: o
 * ATENDIMENTO tem `price_table.read` e não tem `rental_policy.read`, então para
 * ele a política responde 403 sempre. Mostrar erro nesse caso seria alarme
 * sobre uma permissão que ele nunca teve.
 */
type EstadoDaOcupacao = 'CARREGANDO' | 'LIGADO' | 'DESLIGADO' | 'ILEGIVEL' | 'SEM_ACESSO'

/**
 * O aviso vem ANTES da lista de propósito: quem chega aqui para criar uma regra
 * precisa saber que ela não vai agir antes de escrevê-la.
 */
function AvisoDoPrecoPorOcupacao({
  estado,
}: {
  readonly estado: EstadoDaOcupacao
}): ReactElement | null {
  switch (estado) {
    case 'SEM_ACESSO': {
      return null
    }

    case 'CARREGANDO': {
      return (
        <p role="status" aria-live="polite" className="text-xs text-fg-muted">
          Verificando na política se o preço por ocupação está ligado…
        </p>
      )
    }

    case 'ILEGIVEL': {
      return (
        <Alert variant="neutral">
          <AlertTitle>Não foi possível ler a política de locação</AlertTitle>
          <AlertDescription>
            As regras abaixo continuam listadas, mas esta tela não consegue dizer se o motor está
            consultando a camada de ocupação. Quem responde isso é a tela de política de locação.
          </AlertDescription>
        </Alert>
      )
    }

    case 'DESLIGADO': {
      return (
        <Alert variant="warning">
          <AlertTitle>O preço por ocupação está desligado na política</AlertTitle>
          <AlertDescription>
            Enquanto ele estiver fechado, o motor nem consulta estas regras: nenhuma cotação muda
            por causa delas. Cadastrar agora é legítimo — é assim que se prepara a virada —, mas o
            interruptor fica na tela de política de locação, e é lá que a camada passa a valer.
          </AlertDescription>
        </Alert>
      )
    }

    case 'LIGADO': {
      return (
        <Alert variant="info">
          <AlertDescription>
            O preço por ocupação está ligado: estas regras entram em toda cotação da categoria que
            elas alcançam.
          </AlertDescription>
        </Alert>
      )
    }
  }
}

interface PropsDaAbaDeOcupacoes extends PropsDaAba {
  readonly estadoDaOcupacao: EstadoDaOcupacao
  readonly aoEditar: (regra: RegraDeOcupacao) => void
}

function AbaDeOcupacoes({
  pagina,
  aoMudarDePagina,
  categoryId,
  ativo,
  temFiltro,
  aoLimparFiltros,
  podeCriar,
  podeEditar,
  podeDesativar,
  estadoDaOcupacao,
  aoCriar,
  aoEditar,
}: PropsDaAbaDeOcupacoes): ReactElement {
  const filtros = useMemo(
    () => ({ pagina, tamanho: TAMANHO_DA_PAGINA, categoryId, ativo }),
    [pagina, categoryId, ativo],
  )

  const consulta = useRegrasDeOcupacao(filtros)
  const desativar = useDesativarRegraDeOcupacao()
  const atualizar = useAtualizarRegraDeOcupacao()

  const itens = useMemo(() => consulta.data?.itens ?? [], [consulta.data])
  const conflitos = useMemo(() => conflitosDeOcupacao(itens), [itens])

  const ocupado = desativar.isPending || atualizar.isPending

  function alternarVigencia(regra: RegraDeOcupacao): void {
    const opcoes = {
      onSuccess: () => {
        avisarVigencia(regra.nome, regra.ativo)
      },
      onError: avisarFalhaDeVigencia,
    }

    if (regra.ativo) {
      desativar.mutate(regra.id, opcoes)
      return
    }

    atualizar.mutate({ id: regra.id, dados: { ativo: true } }, opcoes)
  }

  const colunas = useMemo<ColumnDef<RegraDeOcupacao>[]>(
    () => [
      {
        id: 'nome',
        header: 'Regra',
        accessorFn: (linha: RegraDeOcupacao) => linha.nome,
        cell: ({ row }) => (
          <CelulaDaRegra nome={row.original.nome} escopo={escopoDaRegra(row.original.categoria)} />
        ),
      },
      {
        id: 'faixa',
        header: 'Ocupação prevista',
        accessorFn: (linha: RegraDeOcupacao) => linha.ocupacaoMinimaEmPontosBase,
        cell: ({ row }) => {
          const regra: RegraDeOcupacao = row.original
          const cruzam = regra.ativo ? (conflitos.get(regra.id) ?? []) : []
          const [primeira, ...resto] = cruzam

          return (
            <div className="min-w-0 space-y-1">
              <p className="whitespace-nowrap text-sm tabular-money text-foreground">
                {formatarPontosBaseComoPercentual(regra.ocupacaoMinimaEmPontosBase)} a{' '}
                {formatarPontosBaseComoPercentual(regra.ocupacaoMaximaEmPontosBase)}
              </p>

              {primeira === undefined ? null : (
                <>
                  <Badge variant="warning">
                    <TriangleAlert aria-hidden="true" />
                    Faixa cruzada
                  </Badge>
                  <p className="max-w-56 text-xs text-fg-secondary">
                    A faixa se cruza com a de “{primeira.nome}”
                    {resto.length > 0 ? ` e mais ${String(resto.length)}` : ''}. Na sobreposição só
                    uma vale, e a escolha não é sua.
                  </p>
                </>
              )}
            </div>
          )
        },
      },
      {
        id: 'multiplicador',
        header: 'Multiplicador',
        meta: { alinhamento: 'direita' },
        accessorFn: (linha: RegraDeOcupacao) => linha.multiplicadorEmPontosBase,
        cell: ({ row }) => (
          <CelulaDeMultiplicador pontosBase={row.original.multiplicadorEmPontosBase} />
        ),
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeVigencia ativo={row.original.ativo} />,
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <AcoesDaRegra
            nome={row.original.nome}
            ativa={row.original.ativo}
            podeEditar={podeEditar}
            podeDesativar={podeDesativar}
            ocupado={ocupado}
            aoEditar={() => {
              aoEditar(row.original)
            }}
            aoAlternar={() => {
              alternarVigencia(row.original)
            }}
          />
        ),
      },
    ],
    // Ver a nota da aba de sazonalidade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditar, podeDesativar, ocupado, conflitos],
  )

  const dados = consulta.data

  return (
    <div className="flex flex-col gap-4">
      <AvisoDoPrecoPorOcupacao estado={estadoDaOcupacao} />

      <TabelaDeDados
        colunas={colunas}
        dados={itens}
        carregando={consulta.isPending}
        erro={consulta.isError ? consulta.error : undefined}
        aoTentarNovamente={() => {
          void consulta.refetch()
        }}
        legenda="Regras de ocupação"
        estadoVazio={
          <EmptyState
            icone={TrendingUp}
            titulo={temFiltro ? 'Nenhuma regra com esses filtros' : 'Nenhuma regra de ocupação'}
            descricao={
              temFiltro
                ? 'Ajuste a categoria ou a situação para ver outras regras.'
                : 'É a camada que cobra mais quando resta pouco carro. Ela só age com o interruptor ligado na política.'
            }
            acao={
              temFiltro ? (
                <Button variant="secondary" onClick={aoLimparFiltros}>
                  Limpar filtros
                </Button>
              ) : podeCriar ? (
                <Button onClick={aoCriar}>
                  <Plus aria-hidden="true" />
                  Nova regra
                </Button>
              ) : undefined
            }
          />
        }
      />

      <Paginacao
        pagina={dados?.pagina ?? pagina}
        tamanho={dados?.tamanho ?? TAMANHO_DA_PAGINA}
        total={dados?.total ?? 0}
        aoMudarDePagina={aoMudarDePagina}
        desabilitado={consulta.isFetching}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// A tela
// ---------------------------------------------------------------------------

export function RegrasDePrecoPage(): ReactElement {
  /**
   * Cada permissão é a do VERBO que a ação usa na API, e não a de um "modo de
   * edição" genérico: criar é `POST` (`create`), editar e pôr em vigor são
   * `PATCH` (`update`), tirar de vigor é `DELETE` (`delete`). A política de
   * locação é outro recurso — `rental_policy` —, e o ATENDIMENTO chega a esta
   * tela sem ela.
   */
  const podeCriar = usePermissao('price_table.create')
  const podeEditar = usePermissao('price_table.update')
  const podeDesativar = usePermissao('price_table.delete')
  const podeLerPolitica = usePermissao('rental_policy.read')

  const [aba, setAba] = useState<CamadaDeRegra>('SAZONALIDADE')
  const [pagina, setPagina] = useState(1)
  const [categoryId, setCategoryId] = useState('')
  const [ativo, setAtivo] = useState<FiltroDeAtivo>('')
  const [priceTableId, setPriceTableId] = useState('')
  const [efeito, setEfeito] = useState<EfeitoDeRegra | ''>('')
  const [vigenteEm, setVigenteEm] = useState('')

  const [dialogoAberto, setDialogoAberto] = useState(false)
  const [alvo, setAlvo] = useState<AlvoDaRegra>({ camada: 'SAZONALIDADE' })

  const categorias = useCategoriasAtivas()
  const tabelas = useTabelasAtivas()

  /**
   * A política é de outro recurso (`rental_policy.read`) e nem todo papel que
   * lê tabela de preço a tem — o ATENDIMENTO é o caso concreto. Nenhuma outra
   * consulta depende dela: quando ela não vem, a aba de ocupação diz o que
   * sabe (nada) em vez de afirmar que o interruptor está desligado.
   */
  const politica = usePoliticaDeLocacao()

  const estadoDaOcupacao: EstadoDaOcupacao = !podeLerPolitica
    ? 'SEM_ACESSO'
    : politica.data !== undefined
      ? politica.data.precoPorOcupacaoAtivo
        ? 'LIGADO'
        : 'DESLIGADO'
      : politica.isError
        ? 'ILEGIVEL'
        : 'CARREGANDO'

  const naSazonalidade = aba === 'SAZONALIDADE'

  const temFiltro =
    categoryId !== '' ||
    ativo !== '' ||
    (naSazonalidade && (priceTableId !== '' || efeito !== '' || vigenteEm !== ''))

  function limparFiltros(): void {
    setCategoryId('')
    setAtivo('')
    setPriceTableId('')
    setEfeito('')
    setVigenteEm('')
    setPagina(1)
  }

  /**
   * Trocar de aba zera a página: a de número 3 da sazonalidade quase nunca
   * existe em antecedência, e a lista voltaria vazia sem que nada explicasse
   * por quê.
   */
  function trocarDeAba(proxima: CamadaDeRegra): void {
    setAba(proxima)
    setPagina(1)
  }

  function abrirCriacao(): void {
    setAlvo({ camada: aba })
    setDialogoAberto(true)
  }

  function abrirEdicao(proximo: AlvoDaRegra): void {
    setAlvo(proximo)
    setDialogoAberto(true)
  }

  const listaDeCategorias = categorias.data?.itens ?? []
  const listaDeTabelas = tabelas.data?.itens ?? []

  const propsDaAba = {
    pagina,
    aoMudarDePagina: setPagina,
    categoryId,
    ativo,
    temFiltro,
    aoLimparFiltros: limparFiltros,
    podeCriar,
    podeEditar,
    podeDesativar,
    aoCriar: abrirCriacao,
  }

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Regras de preço</h1>
        <p className="max-w-prose text-sm text-fg-secondary">
          As quatro camadas que o motor aplica sobre a diária da faixa, nesta ordem: sazonalidade,
          dia da semana, antecedência e ocupação. Elas compõem — o feriado que cai no sábado leva os
          dois multiplicadores.
        </p>
      </div>

      {/* ⚠️ A SUGESTÃO DE IA FICA NESTA TELA, E NÃO NO PAINEL DE INDICADORES.
          É aqui que o gestor cria a regra de sazonalidade — então é aqui que a
          sugestão vale alguma coisa. Num painel de leitura ela seria uma
          curiosidade; ao lado do botão "Nova regra" ela é um argumento.

          Só aparece na aba de SAZONALIDADE: a sugestão fala de multiplicador
          por período, e mostrá-la sobre a aba de dia da semana convidaria a
          aplicar o número na camada errada — as quatro COMPÕEM, e um 1,35 posto
          duas vezes vira 1,82.

          E ela não aplica nada. O caminho continua sendo o mesmo botão de
          sempre, com o mesmo registro em `audit_log` (invariante nº 3). */}
      {naSazonalidade ? (
        <CartaoDeInsight
          tipo="SUGESTAO_DE_PRECO_SAZONAL"
          titulo="Semanas que o histórico sugere reprecificar"
          descricao="Sugestão, não aplicação: confira o número e crie a regra pelo botão “Nova regra”, logo abaixo."
        />
      ) : null}

      {/* Barra de abas. O único amarelo além do CTA é o traço da aba ativa — é
          onde o olho precisa saber que está. */}
      <div
        role="tablist"
        aria-label="Camadas de regra de preço"
        className="-mb-px flex gap-1 overflow-x-auto border-b border-border"
      >
        {CAMADAS_DE_REGRA.map((camada) => {
          const selecionada = camada === aba
          const Icone = ICONE_DA_CAMADA[camada]

          return (
            <button
              key={camada}
              type="button"
              role="tab"
              id={`aba-${camada}`}
              aria-selected={selecionada}
              // Só a aba ativa tem painel montado — apontar para o id de um
              // painel que não existe é referência quebrada para o leitor de tela.
              aria-controls={selecionada ? `painel-${camada}` : undefined}
              onClick={() => {
                trocarDeAba(camada)
              }}
              className={cn(
                'flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-2 text-sm',
                'transition-colors focus-visible:outline-none focus-visible:ring-2',
                'focus-visible:ring-ring',
                selecionada
                  ? 'border-primary font-medium text-foreground'
                  : 'border-transparent text-fg-secondary hover:text-foreground',
              )}
            >
              <Icone className="size-4" aria-hidden="true" />
              {ROTULO_DA_CAMADA[camada]}
            </button>
          )
        })}
      </div>

      <div
        role="tabpanel"
        id={`painel-${aba}`}
        aria-labelledby={`aba-${aba}`}
        className="flex flex-col gap-4"
      >
        {/* O botão mora DEPOIS das abas, ao lado do resumo da camada: a regra
            nova nasce na camada da aba aberta, e é a aba que o gestor escolhe
            primeiro (QA de 30/10). */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <p className="max-w-prose text-sm text-fg-secondary">{RESUMO_DA_CAMADA[aba]}</p>

          {podeCriar ? (
            <Button onClick={abrirCriacao}>
              <Plus aria-hidden="true" />
              Nova regra
            </Button>
          ) : null}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="filtro-categoria-regras">Categoria</Label>
            <Select
              id="filtro-categoria-regras"
              value={categoryId}
              onChange={(evento) => {
                setPagina(1)
                setCategoryId(evento.target.value)
              }}
              disabled={categorias.isPending || categorias.isError}
            >
              <option value="">{categorias.isPending ? 'Carregando…' : 'Todas'}</option>
              {listaDeCategorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nome}
                </option>
              ))}
            </Select>

            {/* Um seletor desabilitado e mudo pareceria tela travada; a lista de
                regras continua funcionando sem este filtro. */}
            {categorias.isError ? (
              <p className="text-xs text-danger-fg">Não foi possível carregar as categorias.</p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-situacao-regras">Situação</Label>
            <Select
              id="filtro-situacao-regras"
              value={ativo}
              onChange={(evento) => {
                setPagina(1)
                const valor = evento.target.value
                setAtivo(OPCOES_DE_ATIVO.find((item) => item === valor) ?? '')
              }}
            >
              <option value="">Todas</option>
              {OPCOES_DE_ATIVO.map((item) => (
                <option key={item} value={item}>
                  {ROTULO_DA_VIGENCIA[item]}
                </option>
              ))}
            </Select>
          </div>

          {naSazonalidade ? (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="filtro-tabela-regras">Tabela de preço</Label>
                <Select
                  id="filtro-tabela-regras"
                  value={priceTableId}
                  onChange={(evento) => {
                    setPagina(1)
                    setPriceTableId(evento.target.value)
                  }}
                  disabled={tabelas.isPending || tabelas.isError}
                >
                  <option value="">{tabelas.isPending ? 'Carregando…' : 'Todas'}</option>
                  {listaDeTabelas.map((tabela) => (
                    <option key={tabela.id} value={tabela.id}>
                      {tabela.nome}
                    </option>
                  ))}
                </Select>

                {/* Mesmo motivo do filtro de categoria: seletor desabilitado e
                    mudo parece tela travada. A lista de regras continua
                    funcionando sem este filtro. */}
                {tabelas.isError ? (
                  <p className="text-xs text-danger-fg">
                    Não foi possível carregar as tabelas de preço.
                  </p>
                ) : null}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="filtro-efeito-regras">Efeito</Label>
                <Select
                  id="filtro-efeito-regras"
                  value={efeito}
                  onChange={(evento) => {
                    setPagina(1)
                    const valor = evento.target.value
                    setEfeito(EFEITOS_DE_REGRA.find((item) => item === valor) ?? '')
                  }}
                >
                  <option value="">Todos</option>
                  {EFEITOS_DE_REGRA.map((item) => (
                    <option key={item} value={item}>
                      {ROTULO_DO_EFEITO[item]}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="filtro-dia-regras">Regras que cobrem o dia</Label>
                <Input
                  id="filtro-dia-regras"
                  type="date"
                  value={vigenteEm}
                  onChange={(evento) => {
                    setPagina(1)
                    setVigenteEm(evento.target.value)
                  }}
                  className="tabular-money"
                />
                <p className="text-xs text-fg-muted">
                  É assim que se explica um preço: escolha o dia e veja quais regras o disputam.
                </p>
              </div>
            </>
          ) : null}
        </div>

        {aba === 'SAZONALIDADE' ? (
          <AbaDeSazonalidades
            {...propsDaAba}
            priceTableId={priceTableId}
            efeito={efeito}
            vigenteEm={vigenteEm}
            tabelas={listaDeTabelas}
            aoEditar={(regra) => {
              abrirEdicao({ camada: 'SAZONALIDADE', regra })
            }}
          />
        ) : null}

        {aba === 'DIA_DA_SEMANA' ? (
          <AbaDeDiasDaSemana
            {...propsDaAba}
            aoEditar={(regra) => {
              abrirEdicao({ camada: 'DIA_DA_SEMANA', regra })
            }}
          />
        ) : null}

        {aba === 'ANTECEDENCIA' ? (
          <AbaDeAntecedencias
            {...propsDaAba}
            aoEditar={(regra) => {
              abrirEdicao({ camada: 'ANTECEDENCIA', regra })
            }}
          />
        ) : null}

        {aba === 'OCUPACAO' ? (
          <AbaDeOcupacoes
            {...propsDaAba}
            estadoDaOcupacao={estadoDaOcupacao}
            aoEditar={(regra) => {
              abrirEdicao({ camada: 'OCUPACAO', regra })
            }}
          />
        ) : null}
      </div>

      {podeCriar || podeEditar ? (
        <DialogoDeRegra
          aberto={dialogoAberto}
          alvo={alvo}
          aoFechar={() => {
            setDialogoAberto(false)
          }}
        />
      ) : null}
    </div>
  )
}
